import { ICrudLocale } from '@app/i18n/interface';

/**
 * Textos de Reservas. En el BackOffice el pedido se llama «reserva» (terminología de las
 * maquetas del 29-oct): femenino en todo, «la reserva», «Confirmada», «Cancelada».
 * Tuteo informal (B-14, HB-182).
 */
export const ORDER_MANAGEMENT_LOCALE_ES: ICrudLocale = {
  ORDER: {
    LABEL: {
      SINGULAR: 'Reserva',
      PLURAL: 'Reservas',
    },
    FIELD: {
      REFERENCE: 'Referencia',
      NO_NUMBER: 'Sin número (no se ha cobrado)',
      NO_NUMBER_SHORT: 'Sin número',
      CUSTOMER: 'Cliente',
      // Una reserva sin cliente NO es un dato que falte: es una compra de invitado
      // (carrito anónimo o checkout sin cuenta). Un guion lo hacía parecer un hueco.
      CUSTOMER_GUEST: 'Invitado',
      HOLDER: 'Titular',
      EMAIL: 'Correo',
      STATUS: 'Estado',
      TOTAL: 'Total',
      CURRENCY: 'Moneda',
      CREATED_AT: 'Creada',
      UPDATED_AT: 'Actualizada',
      HOTEL: 'Hotel',
      CHECK_IN: 'Entrada',
      INTERNAL_NOTES: 'Notas internas',
      DEADLINE: 'Fecha límite de pago',
      AMOUNT_DUE: 'Importe pendiente',
    },
    // Reservas con un problema con el proveedor (2026-10-12): panel de inicio y listado.
    PROVIDER_ALERTS: {
      TITLE: 'Reservas con problemas en Hotelbeds o Stripe',
      SUBTITLE: 'Lo que las comprobaciones con Hotelbeds y Stripe han encontrado y sigue sin resolver.',
      MISSING: 'Hotelbeds no la tiene',
      MISSING_ACTION: 'La damos por confirmada y el hotel no la va a encontrar: rehazla o avisa al cliente antes de que viaje.',
      ORPHAN: 'Sigue viva en Hotelbeds',
      ORPHAN_ACTION: 'La damos por cancelada y Hotelbeds la mantiene: cancélala con ellos o reclama, o nos la facturan.',
      // Pago diferido (REQ-039).
      CANCEL_FAILED: 'La anulación falló',
      CANCEL_FAILED_ACTION: 'Es una reserva sin pagar que no se pudo anular: sigue viva en Hotelbeds. Anúlala antes de que empiecen los gastos de cancelación, o los paga IBSOA.',
      PAYMENT_STUCK: 'Cobro atascado en Stripe',
      PAYMENT_STUCK_ACTION: 'No se pudo cerrar el cobro en Stripe. NO la anules a mano en Hotelbeds: resuélvelo antes con Stripe. Si el cobro entra, el aviso desaparece solo.',
      CANCEL_CHARGED: 'Hotelbeds cobró la anulación',
      CANCEL_CHARGED_ACTION: 'Hotelbeds cobró por anular una reserva sin pagar; lo asume IBSOA. El importe está en el detalle de la reserva.',
      WHEN_TODAY: 'Entra hoy',
      WHEN_TOMORROW: 'Entra mañana',
      WHEN_DAYS: 'Entra en {{days}} días',
      WHEN_PAST: 'Entró hace {{days}} días',
      REF: 'Localizador de Hotelbeds {{ref}}',
      NO_REF: 'Sin localizador de Hotelbeds',
      DETECTED: 'detectado el',
      NO_HOTEL: 'Hotel sin nombre',
      FAILED_TITLE: 'No se pudieron comprobar las reservas con problemas',
      FAILED_DESC: 'La lista no se ha cargado, así que no sabemos si hay alguna. Recarga la página; si sigue, revisa la API.',
    },
    LIST: {
      TITLE: 'Todas las reservas',
      DESCRIPTION: 'Todas las reservas hechas en la web, con su estado de pago y de confirmación en Hotelbeds.',
      CAPTION: 'Reservas',
      FILTERS: 'Filtrar reservas',
      REFERENCE: 'Referencia',
      REFERENCE_PLACEHOLDER: 'IBSOA-12345678',
      CUSTOMER: 'Cliente',
      CUSTOMER_PLACEHOLDER: 'Nombre o correo',
      CUSTOMER_NOT_FOUND: 'Ningún cliente con ese nombre o correo.',
      CREATED_FROM: 'Creada desde',
      CREATED_TO: 'Creada hasta',
      DATE_PLACEHOLDER: 'dd/mm/aaaa',
      DATE_INVALID: 'Escribe la fecha como dd/mm/aaaa.',
      APPLY: 'Aplicar filtros',
      CLEAR: 'Limpiar',
      STATUS_FILTER: 'Filtrar por estado',
      CHIP: {
        ALL: 'Todas',
        CONFIRMED: 'Confirmadas',
        PENDING_PAYMENT: 'Pendientes de pago',
        BOOKED_UNPAID: 'Reservadas sin pagar',
        FAILED: 'Fallidas',
        PARTIALLY_CONFIRMED: 'Confirmadas a medias',
        CANCELLED: 'Canceladas',
        REFUNDED: 'Reembolsadas',
        EXPIRED: 'Caducadas',
        CARTS: 'Carritos',
      },
      // B-56: se lee como una frase. Antes «Sin carritos mostrarlos».
      SHOW_CARTS: 'Mostrar también los carritos',
      CARTS_NOTE: '«Todas» no incluye los carritos: cestas de la web que nadie ha pagado.',
      NO_DATA: 'No hay reservas con estos filtros.',
      EXPORT_CSV: 'Exportar CSV',
      // Pago diferido, debajo del estado (REQ-050).
      PAY_BY: 'Pagar antes del {{date}}',
      HOTEL_TIME: 'hora del hotel',
      AMOUNT_DUE: 'Pendiente: {{amount}}',
      // El rojo no basta: también se dice (QA visual 31-10).
      DUE_SOON: 'Vence en menos de 24 h',
    },
    // M-21: por qué una reserva está como está, debajo de su estado.
    REASON: {
      PAYMENT_FAILED: 'El pago no se completó',
      BOOKING_FAILED_CHARGED: 'Cobrada y sin reserva en Hotelbeds',
      BOOKING_FAILED_REFUND_PENDING: 'Sin reserva en Hotelbeds, devolución en curso',
      BOOKING_FAILED_PARTIALLY_REFUNDED: 'Sin reserva en Hotelbeds, cobro devuelto en parte',
      BOOKING_FAILED_REFUNDED: 'Sin reserva en Hotelbeds, cobro devuelto',
      ROOMS_UNCONFIRMED: '{{count}} de {{total}} habitaciones sin confirmar',
      EXPIRED: 'Sin pago en el plazo',
      BOOKED_UNPAID: 'Sin cobrar: el cliente paga antes de la fecha límite',
      CANCELLATION_IN_PROGRESS: 'Cancelación en curso, sin coste',
    },
    // «Reservar y pagar después» en el detalle (REQ-050).
    PAY_LATER: {
      TITLE: 'Pagar después',
      DESCRIPTION: 'Reservada en Hotelbeds sin cobrar. Si el cliente no paga antes de la fecha límite, se anula sola y sin coste.',
      DEADLINE: 'Fecha límite de pago',
      HOTEL_TIME: 'hora del hotel',
      UTC: '{{date}} UTC',
      AMOUNT_DUE: 'Importe pendiente',
      NOTHING_DUE: 'Nada pendiente',
      CANCELLATION: 'Cancelación en curso',
      CANCELLATION_SINCE: 'Solicitada el {{date}}. La termina el proceso automático, sin coste; ya no se puede pagar.',
      REMINDER_72: 'Recordatorio de 72 h',
      REMINDER_24: 'Recordatorio de 24 h',
      REMINDER_NOT_SENT: 'No enviado',
    },
    TIMELINE: {
      TITLE: 'Historial',
      DESCRIPTION: 'Lo que ha pasado con la reserva, lo más reciente arriba.',
      TIMEZONE: 'Horas en tu zona ({{zone}})',
      EMPTY: 'Todavía no hay nada en el historial de esta reserva.',
      BY: 'Por {{actor}}',
      SYSTEM: 'Por el proceso automático',
      NO_DATE: 'Sin fecha registrada',
      ROOMS: 'Habitación {{rooms}}',
      ROOM_UNKNOWN: 'Una habitación de la reserva',
      NO_REFERENCE: 'Hotelbeds no tiene localizador para esta habitación.',
      REFERENCE_FOUND: 'Localizador en Hotelbeds: {{ref}}',
      EXPECTED: 'Esperábamos el localizador {{ref}}.',
      FOUND: 'Hotelbeds tiene: {{refs}}',
      REFUND_ID: 'Devolución en Stripe {{id}}',
      STATUS_TO: 'La reserva pasa a «{{status}}»',
      AMOUNT: 'Importe: {{amount}}',
      REPRICE_AMOUNT: 'Diferencia de coste: {{amount}}',
      PROVIDER_FAILED: 'Hotelbeds devolvió un error',
      PROVIDER_STATUS: '{{operation}}: respuesta {{status}}',
      PROVIDER_NO_RESPONSE: '{{operation}}: sin respuesta',
      PROVIDER_OPEN: 'Ver la respuesta de Hotelbeds',
      EVENT: {
        ORDER_CREATED: 'Reserva creada en la web',
        PAYMENT_INITIATED: 'Pago iniciado',
        STATUS_CHANGE: 'Cambio de estado',
        CONFIRMATION_SENT: 'Confirmación enviada',
        CONFIRMATION_RESENT: 'Confirmación reenviada',
        NOTES_UPDATE: 'Notas internas actualizadas',
        REFUND: 'Reembolso',
        REPRICE_ACCEPTED: 'Hotelbeds ajustó el precio al reservar',
        // La vigilancia de reservas (IB-242). Se nombran por lo que PASA, no por
        // el proceso que lo encontró: quien lee el historial quiere saber qué
        // ocurre con esta reserva, no cómo se llama el barrido que lo vio.
        BOOKING_RECHECK: 'Reserva recomprobada en Hotelbeds',
        BOOKING_MISSING: 'Hotelbeds no tiene la reserva',
        BOOKING_ORPHAN: 'Sigue viva en Hotelbeds',
        PAYMENT_SUCCEEDED: 'Pago completado',
        BOOKING_CONFIRMED: 'Hotelbeds confirmó la reserva',
        BOOKING_FAILED: 'La reserva falló en Hotelbeds',
      },
    },
    DETAIL: {
      TITLE: 'Detalle de la reserva',
      TITLE_REFERENCE: 'Reserva {{reference}}',
      TITLE_NO_REFERENCE: 'Reserva sin número',
      CREATED: 'Creada el {{date}} (hora de {{zone}}).',
      NOT_FOUND: 'No se encontró la reserva.',
      BOOKED_AT: 'Fecha de la reserva',
      PLACED_AT: 'Pasó al pago',
      PAID_AT: 'Cobrada',
      CONFIRMED_AT: 'Confirmada por Hotelbeds',
      BACK: 'Volver a reservas',
      SUMMARY: 'Cliente',
      NO_TRAVELERS: 'Sin huéspedes registrados.',
    },
    DOCUMENTS: {
      TITLE: 'Voucher y recibo',
      DESCRIPTION: 'Los mismos documentos que recibe el cliente.',
      LOADING: 'Comprobando los documentos…',
      VOUCHER: 'Voucher',
      RECEIPT: 'Recibo',
      VIEW_VOUCHER: 'Ver voucher',
      DOWNLOAD_PDF: 'Descargar PDF',
      VIEW_RECEIPT: 'Ver recibo',
      PDF_OFF: 'El PDF no está disponible en este entorno; el voucher sí.',
      REASON: {
        PENDING_PAYMENT: 'Pendiente de pago: no hay voucher ni recibo hasta que se cobre.',
        NO_CONFIRMED_BOOKING: 'No hay ninguna habitación confirmada con localizador.',
        NOT_PAID: 'Sin recibo: la reserva no consta cobrada.',
        LINK_EXPIRED: 'El enlace del voucher caducó (30 días después de la salida).',
        NOT_CONFIGURED: 'Este entorno no tiene la web configurada.',
      },
      FAILED: {
        TITLE: 'No se han podido comprobar los documentos',
        DESC: 'Recarga la página; si sigue fallando, avisa al equipo técnico.',
      },
    },
    RECEIPT: {
      TITLE: 'Recibo de pago',
      BACK: 'Volver a la reserva',
      REFERENCE: 'Referencia',
      ORDERED_AT: 'Fecha del pedido',
      PAID_AT: 'Fecha del cobro',
      HOLDER: 'Titular',
      ISSUER: 'Emisor',
      PAYMENT_REFERENCE: 'Referencia del cobro',
      LINES: 'Lo que se pagó',
      STAY: 'Del {{checkIn}} al {{checkOut}} · {{nights}} noches',
      INCLUDED: 'Impuestos y cargos incluidos',
      AT_HOTEL: 'A pagar en el alojamiento, aparte: {{amount}}',
      AT_HOTEL_UNKNOWN: 'Hay cargos a pagar en el alojamiento, aparte, de importe no conocido.',
      FREE_CANCELLATION: 'Cancelación gratuita hasta el {{until}} (hora local del alojamiento).',
      NON_REFUNDABLE: 'Sin cancelación gratuita.',
      TOTAL: 'Total pagado',
      REFUNDED: 'Reembolsado',
      FAILED: {
        TITLE: 'No se ha podido cargar el recibo',
        DESC: 'Puede que la reserva aún no conste cobrada. Vuelve a la reserva y comprueba su pago.',
      },
    },
    ORIGIN: {
      TITLE: 'Origen',
      DESCRIPTION: 'De dónde llegó la visita que acabó en esta compra. Solo para administración.',
      SOURCE: 'Origen',
      MEDIUM: 'Medio',
      CAMPAIGN: 'Campaña',
      TERM: 'Término',
      CONTENT: 'Contenido',
      LANDING_PATH: 'Página de entrada',
      REFERRER_HOST: 'Sitio de procedencia',
      AD_CLICK: 'Clic en anuncio',
      NO_AD_CLICK: 'Ninguno',
      CLICK_IDS_PURGED: 'Los identificadores del clic ya se borraron por el plazo de conservación.',
      CLICKED_AT: 'Momento del clic',
      CAPTURED_AT: 'Registrado',
      NONE: 'Sin datos de origen',
      NONE_WHY: 'No lo tienen las reservas anteriores al 2 de noviembre de 2026 ni las de visitantes que no aceptaron las cookies.',
      CLICK_ID: {
        GCLID: 'Google Ads',
        GBRAID: 'Google Ads (iOS)',
        WBRAID: 'Google Ads (web a app)',
        FBCLID: 'Meta',
      },
    },
    INTERNAL: {
      TITLE: 'Importes internos',
      DESCRIPTION: 'Coste y margen, solo para administración.',
      NET_AMOUNT: 'Neto (coste)',
      MARKUP_AMOUNT: 'Margen',
      FEES_AMOUNT: 'Comisiones',
    },
    PAYMENT: {
      TITLE: 'Pago (Stripe)',
      STATUS: 'Estado del pago',
      AMOUNT: 'Importe',
      INTENT_ID: 'Identificador',
      NONE: 'Sin pago registrado.',
    },
    ITEMS: {
      TITLE: 'Hotel y habitaciones',
      ROOM: 'Habitación {{n}}',
      CHECK_IN: 'Entrada',
      CHECK_OUT: 'Salida',
      NIGHTS: '{{n}} noches',
      PROVIDER: 'Proveedor',
      BOOKING_REF: 'Localizador de Hotelbeds',
      HOTEL_CONFIRMATION: 'Nº de confirmación del hotel',
      ROOM_NAME: 'Habitación',
      BOARD_NAME: 'Régimen (boardName)',
      RATE_COMMENTS: 'Comentarios de la tarifa (de CheckRate)',
      RAW_NOTE: 'Los textos en cuadro gris son los que envía Hotelbeds, sin traducir ni reformular.',
      SALE_PRICE: 'Precio de venta',
      TRAVELERS: 'Huéspedes',
      NONE: 'Esta reserva no tiene habitaciones.',
      UNAVAILABLE: 'Hotelbeds dejó de ofrecer esta tarifa el {{date}}.',
      FAILED_TITLE: 'Hotelbeds no confirmó esta habitación',
      FAILED_CALL: '{{operation}}: respuesta {{status}} el {{date}}.',
      FAILED_NO_RESPONSE: '{{operation}}: sin respuesta el {{date}}.',
      PROVIDER_ERROR_AT: 'Respuesta de Hotelbeds del {{date}}.',
      FAILED_OPEN: 'Ver la respuesta de Hotelbeds',
      FAILED_LOADING: 'Buscando el motivo en las llamadas a Hotelbeds…',
      FAILED_UNKNOWN: 'No hay ninguna llamada fallida registrada que lo explique. Revisa las llamadas de esta reserva.',
      FAILED_LOOKUP_ERROR: 'No se pudieron leer las llamadas a Hotelbeds para buscar el motivo.',
      CANCEL_CHARGED_TITLE: 'Hotelbeds cobró por anular esta habitación',
      CANCEL_CHARGED_DESC: 'Cargo de {{amount}} al anular una reserva sin pagar. Lo asume IBSOA: no se repercute al cliente.',
    },
    TRAVELER: {
      NAME: 'Nombre',
      TYPE: 'Tipo',
      AGE: 'Edad',
      AGE_VALUE: '{{age}} años',
      TYPE_AD: 'adulto',
      TYPE_CH: 'niño',
    },
    STATUS: {
      CANCELLED: 'Cancelada',
      CART: 'Carrito',
      COMPLETED: 'Completada',
      CONFIRMED: 'Confirmada',
      FAILED: 'Fallida',
      PAID: 'Pagada',
      PARTIALLY_CONFIRMED: 'Confirmada a medias',
      PENDING_PAYMENT: 'Pendiente de pago',
      REFUNDED: 'Reembolsada',
      EXPIRED: 'Caducada',
      BOOKED_UNPAID: 'Reservada, pendiente de pago',
    },
    ITEM_STATUS: {
      CANCELLED: 'Cancelada',
      COMPLETED: 'Completada',
      CONFIRMED: 'Confirmada',
      FAILED: 'Fallida',
      PARTIALLY_REFUNDED: 'Reembolsada en parte',
      PENDING: 'Pendiente',
      REFUNDED: 'Reembolsada',
    },
    PAYMENT_STATUS: {
      FAILED: 'Fallido',
      PARTIALLY_REFUNDED: 'Devuelto en parte',
      PROCESSING: 'En proceso',
      REFUNDED: 'Devuelto',
      REFUND_PENDING: 'Devolución pendiente',
      REQUIRES_PAYMENT: 'Sin pagar',
      SUCCEEDED: 'Cobrado',
    },
    VERTICAL: {
      CRUISE: 'Crucero',
      FLIGHT: 'Vuelo',
      HOTEL: 'Hotel',
      TRANSFER: 'Traslado',
    },
    ACTION: {
      TITLE: 'Acciones',
      DESCRIPTION: 'Lo que puedes hacer con esta reserva.',
      STATUS: {
        TITLE: 'Cambiar estado',
        PLACEHOLDER: 'Elige el nuevo estado',
        SUBMIT: 'Cambiar estado',
        CONFIRM_TITLE: '¿Cambiar el estado de la reserva?',
        CONFIRM_TEXT: 'La reserva pasará a «{{status}}».',
        SUCCESS: 'Estado de la reserva actualizado.',
      },
      REFUND: {
        TITLE: 'Reembolso manual',
        LABEL: 'Importe a reembolsar ({{currency}})',
        HINT: 'Como máximo {{max}}.',
        PLACEHOLDER: '0,00',
        SUBMIT: 'Reembolsar',
        CONFIRM_TITLE: '¿Reembolsar {{amount}}?',
        CONFIRM_TEXT: 'Se devolverán <strong>{{amount}}</strong> a la tarjeta del cliente. No se puede deshacer.',
        CONFIRM_BUTTON: 'Reembolsar',
        SUCCESS: 'Reembolso emitido.',
      },
      NOTES: {
        PLACEHOLDER: 'Solo las ve el equipo',
        SUBMIT: 'Guardar notas',
        SUCCESS: 'Notas internas guardadas.',
      },
      RESEND: {
        TITLE: 'Voucher',
        HINT: 'Reenvía al cliente el correo de confirmación, que lleva el enlace a su voucher.',
        SUBMIT: 'Reenviar confirmación y voucher',
        CONFIRM_TITLE: '¿Reenviar la confirmación y el voucher?',
        CONFIRM_TEXT: 'Se reenviará el correo de confirmación a «{{recipient}}».',
        CONFIRM_TEXT_CONTACT: 'Se reenviará el correo de confirmación a la dirección de contacto de la reserva.',
        CONFIRM_BUTTON: 'Reenviar',
        SUCCESS: 'Confirmación reenviada.',
      },
      CANCEL: {
        TITLE: 'Cancelar la reserva',
        // Pago diferido con la cancelación ya solicitada: la API respondería ORDER_CANCELLATION_PENDING.
        IN_PROGRESS: 'La cancelación ya está en curso: la termina el proceso automático, sin coste. No hace falta cancelarla desde aquí.',
        COST_TODAY: 'Coste de cancelar hoy',
        NO_COST: 'Sin coste',
        SERVICE_FEE: 'Cargo de gestión de IBSOA',
        SERVICE_FEE_NONE: 'No se aplica al cancelar desde aquí',
        REFUND: 'Importe a devolver',
        // Pendiente de decidir (requisitos de Hotelbeds, punto 1): no se enseña una cifra.
        REFUND_PENDING: 'pendiente de definir',
        // Pago diferido: no hubo cobro, así que no hay nada que devolver.
        REFUND_NONE: 'Nada: no se ha cobrado',
        FREE_UNTIL: 'Cancelación gratuita hasta',
        NO_FREE: 'Sin cancelación gratuita',
        UTC: '{{date}} UTC',
        LOCAL_NOTE: 'hora local del alojamiento',
        NOTE: 'Calculado con la política de cancelación guardada en cada habitación. El importe se confirma al cancelar.',
        LOADING: 'Calculando el coste de cancelar…',
        RETRY: 'Volver a calcular',
        SUBMIT: 'Cancelar reserva',
        CONFIRM_TITLE: '¿Cancelar la reserva {{reference}}?',
        CONFIRM_TEXT: 'Coste de cancelar hoy: <strong>{{cost}}</strong><br>Importe a devolver: <strong>pendiente de definir</strong><br><br>Se cancela en Hotelbeds. No se puede deshacer.',
        CONFIRM_TEXT_UNPAID: 'La reserva no se ha cobrado: se anula en Hotelbeds sin coste y no hay nada que devolver.<br><br>No se puede deshacer.',
        CONFIRM_BUTTON: 'Cancelar la reserva',
        KEEP: 'Mantener la reserva',
        SUCCESS: 'Reserva cancelada.',
      },
      ERROR: {
        GENERIC: 'No se pudo completar la acción. Inténtalo de nuevo.',
        CONFLICT: 'La acción no vale para el estado actual de la reserva.',
        INVALID: 'Los datos de la acción no son válidos.',
        INVALID_AMOUNT: 'El importe tiene que ser mayor que 0 y no superar el máximo reembolsable.',
        REFUND_EXCEEDS_PAYMENT: 'El importe supera lo que se puede devolver del pago.',
        QUOTE: 'No se pudo calcular el coste de cancelar.',
        NO_RECIPIENT: 'La reserva no tiene un correo al que mandar la confirmación.',
        RATE_LIMITED: 'La confirmación se reenvió hace poco. Espera un poco antes de reintentarlo.',
        NOT_FOUND: 'No se encontró la reserva.',
        // Pago diferido: cancelar una reserva sin pagar.
        STATE_CHANGED: 'La reserva cambió mientras la mirabas (un pago o el proceso automático). Recárgala y vuelve a intentarlo.',
        CANCELLATION_PENDING: 'La cancelación ya está en curso: la termina el proceso automático, sin coste.',
        // Sirve a las dos cancelaciones: la de una reserva cobrada (Hotelbeds no respondió) y
        // la de una sin pagar (no se pudo cerrar su cobro en Stripe). En ninguna se anuló nada.
        CANCELLATION_FAILED: 'No se pudo cancelar ahora: Hotelbeds o Stripe no respondieron y no se ha anulado nada. Inténtalo de nuevo más tarde.',
      },
    },
  },
};
